<script setup lang="ts">
  // Collapsed "Detalle técnico" card: the only place where accounting
  // jargon (attribution difference, expected/counted cash, over/short)
  // survives for power users. Collapsed by default.
  import { computed, getCurrentInstance, ref } from 'vue';
  import { IDayCloseReportRow } from './day-close-report.types';
  import { IDayClose } from '@/interfaces/IDayClose';
  import { displayPrice } from '@/utils';

  const props = defineProps<{
    payload: IDayClose;
    currencySymbol: string;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const expanded = ref(false);

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  function money(value: number): string {
    return `${props.currencySymbol}${displayPrice(value, true)}`;
  }

  function formatBusinessDate(dateStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  function formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
      timeZone: props.payload.header.timeZone,
      hour12: false,
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const rows = computed<IDayCloseReportRow[]>(() => {
    const { kpis, waterfall, tenders, collections, adjustments } =
      props.payload;
    const list: IDayCloseReportRow[] = [
      {
        key: 'net-sales',
        label: i18nT('v2Stats.dayClose.netSales'),
        value: money(waterfall.netSales),
      },
      {
        key: 'collected',
        label: i18nT('v2Stats.dayClose.collectedSales'),
        value: money(kpis.collected),
      },
    ];

    const adjustmentRows: Array<{
      key: string;
      labelKey: string;
      entry: { count: number; amount: number };
    }> = [
      {
        key: 'canceled-orders',
        labelKey: 'canceledOrders',
        entry: adjustments.canceledOrders,
      },
      {
        key: 'canceled-items',
        labelKey: 'canceledItems',
        entry: adjustments.canceledItems,
      },
      {
        key: 'fully-discounted',
        labelKey: 'fullyDiscounted',
        entry: adjustments.fullyDiscountedOrders,
      },
      {
        key: 'reimbursed-kept',
        labelKey: 'reimbursedKept',
        entry: adjustments.reimbursedKept,
      },
    ];
    const activeAdjustments = adjustmentRows.filter(
      row => row.entry.count > 0
    );
    if (activeAdjustments.length) {
      list.push({
        key: 'adjustments-header',
        label: i18nT('v2Stats.dayClose.sectionAdjustments'),
        subheader: true,
      });
      for (const row of activeAdjustments) {
        list.push({
          key: row.key,
          label: `${i18nT(`v2Stats.dayClose.${row.labelKey}`)} (${
            row.entry.count
          })`,
          value: money(row.entry.amount),
          indent: true,
        });
      }
    }

    const tipRows = props.payload.tipsByBucket.filter(row => row.amount !== 0);
    if (tipRows.length) {
      list.push({
        key: 'tips-header',
        label: i18nT('v2Stats.dayClose.tipsByBucket'),
        subheader: true,
      });
      for (const row of tipRows) {
        list.push({
          key: `tips-${row.bucket}`,
          label: i18nT(`v2Stats.bucket.${row.bucket}`),
          value: money(row.amount),
          indent: true,
        });
      }
    }

    if (tenders.comps.invitation.count > 0) {
      list.push({
        key: 'comp-invitation',
        label: `${i18nT('v2Stats.dayClose.compInvitations')} (${
          tenders.comps.invitation.count
        })`,
        value: money(tenders.comps.invitation.amount),
      });
    }
    if (tenders.comps.employee.count > 0) {
      list.push({
        key: 'comp-employee',
        label: `${i18nT('v2Stats.dayClose.compEmployee')} (${
          tenders.comps.employee.count
        })`,
        value: money(tenders.comps.employee.amount),
      });
    }

    list.push({
      key: 'attribution',
      label: i18nT('v2Stats.dayClose.attributionDifference'),
      value: money(collections.attributionDifference),
      note: i18nT('v2Stats.dayClose.attributionNote'),
    });

    props.payload.cashSessions.forEach((session, index) => {
      if (
        session.cashExpected == null &&
        session.cashCounted == null &&
        session.overShort == null
      ) {
        return;
      }
      // statusId (index fallback) disambiguates cajas with several
      // sessions on the same business day (e.g. split shifts).
      const sessionKey = `session-${session.cajaId}-${
        session.statusId ?? index
      }`;
      list.push({
        key: sessionKey,
        label: session.cajaName,
        subheader: true,
      });
      if (session.cashExpected != null) {
        list.push({
          key: `${sessionKey}-expected`,
          label: i18nT('v2Stats.dayClose.cashExpected'),
          value: money(session.cashExpected),
          indent: true,
        });
      }
      if (session.cashCounted != null) {
        list.push({
          key: `${sessionKey}-counted`,
          label: i18nT('v2Stats.dayClose.cashCounted'),
          value: money(session.cashCounted),
          indent: true,
        });
      }
      if (session.overShort != null) {
        list.push({
          key: `${sessionKey}-overshort`,
          label: i18nT('v2Stats.dayClose.overShort'),
          value: money(session.overShort),
          indent: true,
        });
      }
    });

    return list;
  });

  const windowLine = computed(() => {
    const header = props.payload.header;
    return i18nT('v2Stats.dayClose.windowFootnote', {
      date: formatBusinessDate(header.businessDate),
      from: formatDateTime(header.windowFrom),
      to: formatDateTime(header.windowTo),
    });
  });

  const sourceNote = computed(() =>
    props.payload.collections.source === 'estimated'
      ? i18nT('v2Stats.dayClose.estimatedNote')
      : null
  );
</script>

<template>
  <section class="day-close-tech-detail">
    <button
      type="button"
      class="tech-toggle"
      @click="expanded = !expanded"
    >
      <span class="tech-toggle-title">
        {{ $t('v2Stats.dayClose.techDetail') }}
      </span>
      <i
        class="tech-toggle-icon mdi"
        :class="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
      />
    </button>

    <div v-if="expanded" class="tech-body">
      <div class="tech-rows">
        <div
          v-for="row in rows"
          :key="row.key"
          class="tech-row"
          :class="{ 'is-indent': row.indent, 'is-subheader': row.subheader }"
        >
          <div class="row-main">
            <span class="row-label">{{ row.label }}</span>
            <span v-if="row.value" class="row-value">{{ row.value }}</span>
          </div>
          <p v-if="row.note" class="row-note">{{ row.note }}</p>
        </div>
      </div>
      <p class="tech-footnote">{{ windowLine }}</p>
      <p v-if="sourceNote" class="tech-footnote">{{ sourceNote }}</p>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .day-close-tech-detail {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;

    .tech-toggle {
      @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-0 tw-text-left;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .tech-toggle-title {
        @apply tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .tech-toggle-icon {
        @apply tw-text-lg tw-text-neutral-400;
      }
    }

    .tech-body {
      @apply tw-mt-2;

      .tech-rows {
        .tech-row {
          @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-py-1.5;

          &:last-child {
            @apply tw-border-b-0;
          }

          .row-main {
            @apply tw-flex tw-items-baseline tw-justify-between;

            .row-label {
              @apply tw-mr-4 tw-text-sm tw-text-neutral-600;
            }

            .row-value {
              @apply tw-whitespace-nowrap tw-text-sm tw-font-medium tw-tabular-nums tw-text-neutral-800;
            }
          }

          .row-note {
            @apply tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-neutral-400;
          }

          &.is-subheader {
            @apply tw-pt-3;

            .row-main {
              .row-label {
                @apply tw-font-semibold tw-text-neutral-800;
              }
            }
          }

          &.is-indent {
            .row-main {
              @apply tw-pl-4;
            }
          }
        }
      }

      .tech-footnote {
        @apply tw-mb-0 tw-mt-2 tw-text-xs tw-text-neutral-400;
      }
    }
  }
</style>
